import { ActionTree, Commit } from 'vuex';
import { RootState } from '@/plugins/store';
import { PreavisInterventionState } from './state';
import { PREAVIS } from './mutation-types';
import { PreavisInterventionModuleAction } from './actions-types';
import PreavisInterventionService from '@/modules/PreavisIntervention/services/PreavisInterventionService';
import { CreatePreavisInterventionDTO } from '@/models/PreavisIntervention/CreatePreavisInterventionDTO';
import { UpdatePreavisInterventionDTO } from '@/models/PreavisIntervention/UpdatePreavisInterventionDTO';
import { UpdateActivationPreavisInterventionDTO } from '@/models/PreavisIntervention/UpdateActivationPreavisInterventionDTO';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import ErrorManager from '@/common/utils/ErrorManager';

export const actions: ActionTree<PreavisInterventionState, RootState> = {
    async [PreavisInterventionModuleAction.GET_LIST](
        { commit },
        payload: { siteIds?: number | number[]; equipementIds?: number | number[]; sort?: string } = {}
    ) {

        commit(PREAVIS.SET_LOADING_LIST, true);
        commit(PREAVIS.SET_ERROR, null);
        commit(PREAVIS.SET_FILTERS, {
            siteIds: payload?.siteIds,
            equipementIds: payload?.equipementIds,
        });
        try {
            const { data } = await PreavisInterventionService.getPreavisInterventions(
                payload?.siteIds,
                payload?.equipementIds,
                payload?.sort
            );
            commit(PREAVIS.SET_LIST, data);
        } catch (e) {
            commit(PREAVIS.SET_ERROR, e);
            await onError(e, commit);
        } finally {
            commit(PREAVIS.SET_LOADING_LIST, false);
        }
    },

    async [PreavisInterventionModuleAction.CREATE]({ commit, dispatch }, dto: CreatePreavisInterventionDTO) {
        commit(PREAVIS.SET_SUBMITTING, true);
        commit(PREAVIS.SET_ERROR, null);
        try {
            await PreavisInterventionService.createPreavisIntervention(dto);
            await dispatch(PreavisInterventionModuleAction.GET_LIST);
        } catch (e) {
            commit(PREAVIS.SET_ERROR, 'create_failed');
            await onError(e, commit);
        } finally {
            commit(PREAVIS.SET_SUBMITTING, false);
        }
    },

    async [PreavisInterventionModuleAction.UPDATE](
        { commit },
        payload: { id: number; dto: UpdatePreavisInterventionDTO }
    ) {
        commit(PREAVIS.SET_SUBMITTING, true);
        commit(PREAVIS.SET_ERROR, null);
        try {
            const { data } = await PreavisInterventionService.updatePreavisIntervention(payload.id, payload.dto);
            commit(PREAVIS.UPDATE_ITEM, data);
        } catch (e) {
            commit(PREAVIS.SET_ERROR, 'update_failed');
            await onError(e, commit);
        } finally {
            commit(PREAVIS.SET_SUBMITTING, false);
        }
    },

    async [PreavisInterventionModuleAction.UPDATE_ACTIVATION](
        { commit },
        payload: { id: number; dto: UpdateActivationPreavisInterventionDTO }
    ) {
        commit(PREAVIS.SET_ERROR, null);
        try {
            const { data } = await PreavisInterventionService.updateActivationPreavisIntervention(
                payload.id,
                payload.dto
            );
            commit(PREAVIS.UPDATE_ITEM, data);
        } catch (e) {
            commit(PREAVIS.SET_ERROR, 'update_activation_failed');
            await onError(e, commit);
        }
    },

    async [PreavisInterventionModuleAction.DELETE]({ commit }, id: number) {
        commit(PREAVIS.SET_SUBMITTING, true);

        commit(PREAVIS.SET_ERROR, null);

        try {
            await PreavisInterventionService.deletePreavisIntervention(id);
            commit(PREAVIS.REMOVE_ITEM, id);
        } catch (e) {
            commit(PREAVIS.SET_ERROR, e);
            await onError(e, commit);
        } finally {
            commit(PREAVIS.SET_SUBMITTING, false);
        }
    },
};

async function onError(error: any, commit: Commit) {
    commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
}
